<!-- src/views/MealsView.vue -->
<script setup lang="ts">
import { ref, computed, watch, onMounted } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useQuery, useMutation } from '@vue/apollo-composable'
import { GET_MEALS, GET_MEALS_BY_DATE_RANGE, GET_MEAL_TYPES } from '../graphql/queries'
import { DELETE_MEAL } from '../graphql/mutations'
import { showError, showSuccess } from '../utils/errorHandling'
import { fetchRecipeById } from '../utils/recipeHelpers'
import EditMealModal from '../components/EditMealModal.vue'
import FilterMeals from '../components/FilterMeals.vue'
import MealsTable from '../components/MealsTable.vue'
import ConfirmDialog from '../components/ConfirmDialog.vue'
import CreateMealModal from '../components/CreateMealModal.vue'
import ReuseMealPlanModal from '../components/ReuseMealPlanModal.vue'
import MealsCalendarView from '../components/MealsCalendarView.vue'
import MealsBoardView from '../components/MealsBoardView.vue'
import dayjs from 'dayjs'

type SortOption = 'date-asc' | 'date-desc'
type ViewMode = 'list' | 'calendar' | 'board'

const route = useRoute()
const router = useRouter()

const getViewFromQuery = (): ViewMode =>
  'calendar' in route.query ? 'calendar' : 'grid' in route.query ? 'list' : 'board'
const viewMode = ref<ViewMode>(getViewFromQuery())
const calendarPreselectedDate = ref<string | undefined>(undefined)
const showCreatePopup = ref(false)
const showReusePopup = ref(false)
const showEditPopup = ref(false)
const showDeleteDialog = ref(false)
const selectedMeal = ref<any>(null)
const currentSortOption = ref<SortOption>('date-asc')

const { result: allMeals, refetch: refetchAll } = useQuery(GET_MEALS)

const getInitialDateRange = () => {
  const from = route.query.from as string
  const to = route.query.to as string
  // Arriving from a dashboard day click: browse that specific day (all meals).
  if (from && to) {
    return {
      start_date: dayjs(from).format('DD/MM/YYYY'),
      end_date: dayjs(to).format('DD/MM/YYYY'),
      show_active: false,
    }
  }
  // Default "Upcoming" view: active meals from today onward.
  return {
    start_date: dayjs().startOf('day').format('DD/MM/YYYY'),
    end_date: dayjs().add(1, 'year').format('DD/MM/YYYY'),
    show_active: true,
  }
}

const { result: filteredMeals, loading: loadingFiltered, error: errorFiltered, refetch: refetchFiltered } = useQuery(
  GET_MEALS_BY_DATE_RANGE,
  getInitialDateRange()
)

const { result: allMealTypes, loading: loadMealTypes, refetch: refetchAllTypes } = useQuery(GET_MEAL_TYPES)

// Delete mutation - simplified without cache update
const { mutate: deleteMealMutation, loading: deletingMeal } = useMutation(DELETE_MEAL)


// Edit meal handler
const editMeal = (meal: any) => {
  selectedMeal.value = meal
  showEditPopup.value = true
}

// Delete meal handler
const deleteMeal = (meal: any) => {
  selectedMeal.value = meal
  showDeleteDialog.value = true
}

// Navigate to recipe handler
const goToRecipe = async (recipe_id: string) => {

  try {
    const recipe = await fetchRecipeById(recipe_id)

    if (recipe?.slug) {
      router.push(`/recipes/${recipe.slug}`)
    } else {

      throw new Error('Recipe not found')
    }
  } catch (error) {
    console.error('❌ Error in goToRecipe:', error)
    // Error is already handled by fetchRecipeById
  }
}

// Confirm delete
const confirmDelete = async () => {
  if (!selectedMeal.value) return

  try {
    const result = await deleteMealMutation({
      id: selectedMeal.value.id
    })

    // Only proceed if mutation was successful
    if (result?.data?.deleteMeal === true) {
      showDeleteDialog.value = false
      selectedMeal.value = null

      showSuccess('Meal deleted successfully!')

      // Wait a bit before refetching to avoid race conditions
      setTimeout(async () => {
        try {
          await refetchFiltered()
          await refetchAll()
        } catch (refetchError) {
          console.warn('Refetch after delete failed, but deletion was successful:', refetchError)
        }
      }, 100)
    } else {
      throw new Error('Delete operation failed')
    }
  } catch (error) {
    console.error('Delete meal error:', error)
    showError(error, 'delete meal')
  }
}

// Cancel delete
const cancelDelete = () => {
  showDeleteDialog.value = false
  selectedMeal.value = null
}

const closeEditModal = () => {
  showEditPopup.value = false
  selectedMeal.value = null
}

const handleMealUpdated = () => {
  showEditPopup.value = false
  selectedMeal.value = null
  refetchFiltered()
  refetchAll()
}

// Manejadores de eventos para el filtro
const handleFilter = async (payload: { start_date: string; end_date: string; show_active: boolean }) => {
  await refetchFiltered(payload)
  refetchAllTypes()
}

const handleSort = (sortOption: SortOption) => {
  currentSortOption.value = sortOption
}

const loadMeals = () => loadingFiltered.value || loadMealTypes.value

// Computed properties
const currentMeals = computed(() => {
  const meals = filteredMeals.value?.mealsByDateRange || []

  // Create a copy of the meals array to avoid mutating the original
  const sortedMeals = [...meals]

  // Sort based on the current sort option
  if (currentSortOption.value === 'date-asc') {
    return sortedMeals.sort((a, b) => {
      const dateA = dayjs(a.scheduled_date)
      const dateB = dayjs(b.scheduled_date)
      return dateA.isBefore(dateB) ? -1 : dateA.isAfter(dateB) ? 1 : 0
    })
  } else {
    // date-desc
    return sortedMeals.sort((a, b) => {
      const dateA = dayjs(a.scheduled_date)
      const dateB = dayjs(b.scheduled_date)
      return dateB.isBefore(dateA) ? -1 : dateB.isAfter(dateA) ? 1 : 0
    })
  }
})

const deleteMessage = computed(() => {
  return selectedMeal.value
    ? `Are you sure you want to delete "${selectedMeal.value.title}"? This action cannot be undone.`
    : ''
})

// Handle URL parameters for automatic modal opening
const handleUrlParams = () => {
  const action = route.query.action as string
  const mealId = route.query.id as string
  const recipeId = route.query.recipe as string

  const viewQuery = viewMode.value === 'calendar' ? { calendar: null } : viewMode.value === 'board' ? { board: null } : { grid: null }
  switch (action) {
    case 'create':
      showCreatePopup.value = true
      router.replace({ query: viewQuery })
      break
    case 'edit':
      if (mealId) {
        const meal = currentMeals.value.find((m: any) => m.id === mealId)
        if (meal) {
          editMeal(meal)
          router.replace({ query: viewQuery })
        }
      }
      break
    case 'goToRecipe':
      goToRecipe(recipeId)
      break
    default:
      // No action
      break
  }
}

// Sync URL when view mode changes
watch(viewMode, (mode) => {
  router.replace({ query: mode === 'calendar' ? { calendar: null } : mode === 'board' ? { board: null } : { grid: null } })
})

// Sync view mode when URL changes (browser back/forward)
watch(() => route.query, () => {
  const fromUrl = getViewFromQuery()
  if (fromUrl !== viewMode.value) viewMode.value = fromUrl
})

// Watch for changes in meals data to handle URL params when data loads
watch(currentMeals, (newMeals) => {
  if (newMeals.length > 0 && route.query.action) {
    handleUrlParams()
  }
}, { immediate: true })

// Handle URL params on component mount
onMounted(() => {
  if (route.query.action) {
    handleUrlParams()
  }
})

const handleCalendarCreateMeal = (date: string) => {
  calendarPreselectedDate.value = date
  showCreatePopup.value = true
}

const handleCalendarEditMeal = (meal: any) => {
  editMeal(meal)
}

const handleMealCreated = () => {
  showCreatePopup.value = false
  calendarPreselectedDate.value = undefined
  refetchAll()
  refetchFiltered()
}

const handleMealPlanReused = () => {
  showReusePopup.value = false
  refetchAll()
  refetchFiltered()
}


</script>

<template>
  <div class="page-enter page-container">
    <!-- Header -->
    <div class="page-header">
      <div>
        <h1 class="font-display display-l page-title">Meals</h1>
        <p class="page-subtitle">Plan and track your daily meals</p>
      </div>
      <div class="header-actions">
        <!-- Desktop: view toggle pill -->
        <div class="view-toggle hidden lg:flex">
          <button @click="viewMode = 'board'"
            class="view-toggle-btn"
            :class="{ active: viewMode === 'board' }">
            Board
          </button>
          <button @click="viewMode = 'list'"
            class="view-toggle-btn"
            :class="{ active: viewMode === 'list' }">
            List
          </button>
          <button @click="viewMode = 'calendar'"
            class="view-toggle-btn"
            :class="{ active: viewMode === 'calendar' }">
            Calendar
          </button>
        </div>

        <!-- Mobile: view dropdown (Calendar stays desktop-only) -->
        <div class="view-select-wrap lg:hidden">
          <select v-model="viewMode" class="view-select" aria-label="Meals view">
            <option value="board">Board</option>
            <option value="list">List</option>
          </select>
          <svg class="view-select-chevron" width="14" height="14" fill="none" viewBox="0 0 24 24">
            <path d="M19 9l-7 7-7-7" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" />
          </svg>
        </div>

        <!-- Single pair of action buttons, reused on both breakpoints — CSS repositions them, no duplicate markup -->
        <div class="action-buttons">
          <button @click="showReusePopup = true" class="ds-btn-secondary">Reuse Plan</button>
          <button @click="showCreatePopup = true" class="ds-btn-primary">+ Create Meal</button>
        </div>
      </div>
    </div>

    <!-- Create Meal Modal -->
    <CreateMealModal
      :show="showCreatePopup"
      :preselected-date="calendarPreselectedDate"
      @close="showCreatePopup = false; calendarPreselectedDate = undefined"
      @created="handleMealCreated" />

    <!-- Reuse Meal Plan Modal -->
    <ReuseMealPlanModal :show="showReusePopup" @close="showReusePopup = false" @success="handleMealPlanReused" />

    <!-- Edit Meal Modal -->
    <EditMealModal
      :show="showEditPopup"
      :meal="selectedMeal"
      @close="closeEditModal"
      @updated="handleMealUpdated" />

    <!-- Delete confirmation dialog -->
    <ConfirmDialog :show="showDeleteDialog" title="Delete Meal" :message="deleteMessage" confirm-text="Delete"
      :loading="deletingMeal" @confirm="confirmDelete" @cancel="cancelDelete" />

    <!-- Filters Section — hidden in calendar view on desktop, and fully hidden in board (Board has no use for it, on any breakpoint) -->
    <div class="ds-card filter-card" :class="{ 'lg:hidden': viewMode === 'calendar', hidden: viewMode === 'board' }">
      <FilterMeals
        :initial-from="(route.query.from as string) || undefined"
        :initial-to="(route.query.to as string) || undefined"
        @filter="handleFilter" @sort="handleSort" />
      <div v-if="!loadMeals() && !errorFiltered" class="mt-4 text-sm text-gray-600">
        Showing {{ currentMeals.length }} meal{{ currentMeals.length !== 1 ? 's' : '' }}
        <span v-if="currentMeals.some((m: any) => (m.portions_days ?? 1) > 1)" class="text-gray-400">
          (multi-day meals appear on each scheduled date)
        </span>
      </div>
    </div>

    <!-- Calendar View — desktop only -->
    <div v-if="viewMode === 'calendar'" class="hidden lg:block">
      <MealsCalendarView
        @create-meal-on-date="handleCalendarCreateMeal"
        @edit-meal="handleCalendarEditMeal" />
    </div>

    <!-- Board View — responsive: desktop grid + sidebar, mobile single-day sections + bottom sheet -->
    <div v-if="viewMode === 'board'">
      <MealsBoardView
        :meal-types="allMealTypes?.mealTypes || []"
        @edit-meal="handleCalendarEditMeal" />
    </div>

    <!-- List View — always on small screens when in list mode; only when calendar mode falls back to it on lg+ (Board renders its own mobile layout) -->
    <div v-if="viewMode !== 'board'" :class="viewMode === 'calendar' ? 'space-y-6 lg:hidden' : 'space-y-6'">
      <!-- Loading State -->
      <div v-if="loadMeals()" class="text-center py-12">
        <div class="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto"></div>
        <p class="mt-4 text-gray-500">Loading meals...</p>
      </div>

      <!-- Error State -->
      <div v-else-if="errorFiltered" class="text-center py-12">
        <div class="text-6xl mb-4">⚠️</div>
        <h3 class="text-xl font-semibold text-gray-700 mb-2">Error loading meals</h3>
        <p class="text-gray-500 mb-4">Please try again or check your connection</p>
        <button @click="refetchFiltered()"
          class="btn-primary mobile-touch-target desktop-button-hover desktop-focus-ring" tabindex="0">
          Try Again
        </button>
      </div>

      <!-- Empty State — brand-new user (no meals at all yet) -->
      <div v-else-if="!filteredMeals?.mealsByDateRange?.length && !allMeals?.meals?.length"
        class="text-center py-12">
        <svg class="w-14 h-14 mx-auto mb-4 text-gray-300" viewBox="0 0 24 24" fill="none" stroke="currentColor"
          stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round">
          <rect x="3" y="4" width="18" height="18" rx="2" />
          <path d="M16 2v4M8 2v4M3 10h18M12 14v4M10 16h4" />
        </svg>
        <h3 class="text-xl font-semibold text-gray-700 mb-2">Plan your first meal</h3>
        <p class="text-gray-500 mb-4 max-w-md mx-auto">
          This is where your week comes together. Pick a recipe, choose a day, and it lands on your
          calendar — then you can auto-build a shopping list from it.
        </p>
        <div class="flex items-center justify-center gap-2 flex-wrap">
          <button @click="showCreatePopup = true"
            class="btn-primary mobile-touch-target desktop-button-hover desktop-focus-ring" tabindex="0">
            + Plan a Meal
          </button>
          <button @click="router.push('/recipes')"
            class="btn-secondary mobile-touch-target desktop-button-hover desktop-focus-ring" tabindex="0">
            Browse Recipes
          </button>
        </div>
      </div>

      <!-- Empty State — has meals, but none match the current filter -->
      <div v-else-if="!filteredMeals?.mealsByDateRange?.length"
        class="text-center py-12">
        <div class="text-6xl mb-4">🍽️</div>
        <h3 class="text-xl font-semibold text-gray-700 mb-2">No meals found</h3>
        <p class="text-gray-500 mb-4">Try adjusting your date range or switch to "All meals"</p>
        <div class="space-x-2">
          <button @click="showCreatePopup = true"
            class="btn-primary mobile-touch-target desktop-button-hover desktop-focus-ring" tabindex="0">
            Create Meal
          </button>
        </div>
      </div>

      <!-- Meals Table -->
      <div v-else class="overflow-hidden">
        <MealsTable :meals="currentMeals" :mealTypes="allMealTypes?.mealTypes || []" @edit="editMeal" @delete="deleteMeal"
          @go-to-recipe="goToRecipe" />
      </div>
    </div>
  </div>
</template>

<style scoped>
.page-header   { display: flex; align-items: flex-end; justify-content: space-between; margin-bottom: 40px; flex-wrap: wrap; gap: 16px; }
.page-title    { letter-spacing: -1px; }
.page-subtitle { font-size: 14px; color: #64748B; margin-top: 6px; }
.header-actions { display: flex; align-items: center; gap: 10px; flex-wrap: wrap; }

.view-toggle { background: #EEEEEE; border-radius: 9999px; padding: 3px; }
.view-toggle-btn {
  border: none;
  border-radius: 9999px;
  padding: 6px 18px;
  font-family: Inter, sans-serif;
  font-weight: 700;
  font-size: 12px;
  cursor: pointer;
  transition: all .15s;
  background: transparent;
  color: #64748B;
}
.view-toggle-btn.active { background: white; color: #4F8646; box-shadow: 0 1px 2px rgba(0,0,0,.06); }

.view-select-wrap { position: relative; }
.view-select {
  height: 44px;
  border-radius: 9999px;
  border: 1px solid #E2E8F0;
  background: #EEEEEE;
  padding: 0 40px 0 18px;
  font-family: Inter, sans-serif;
  font-weight: 700;
  font-size: 13px;
  color: #4F8646;
  appearance: none;
  -webkit-appearance: none;
}
.view-select-chevron { position: absolute; right: 16px; top: 50%; transform: translateY(-50%); color: #64748B; pointer-events: none; }

.action-buttons { display: flex; gap: 10px; }

@media (max-width: 1023px) {
  .page-header { align-items: stretch; }
  .header-actions { flex-direction: column; align-items: stretch; }
  .view-select-wrap,
  .view-select { width: 100%; }
  .action-buttons > * { flex: 1; }
}

.filter-card { border-radius: 24px; margin-bottom: 32px; }
</style>
